import { inject, Injectable } from '@angular/core';
import { Decoration } from '../../../../models/configurators-2d/applications/fence/decoration';
import { Color } from '../../../../models/configurators-2d/applications/fence/color';
import { FencePanel } from '../models/fence-panel';
import {
  ConfigurationInstallation
} from '../../../../models/configurators-2d/applications/fence/configuration-installation';
import { ColorStateService } from './color.state.service';
import { InstallationStateService } from './installation.state.service';
import { FencePanelInstallationColors } from '../models/fence-panel-installation-colors';
import { InstallationMixingStateService } from './installation-mixing.state.service';
import { SpacerToBeUsed } from '../../../../enum/fence/spacer-to-be-used.enum';
import { InstallationTypeEnum } from '../../../../enum/fence/installation-type.enum';
import { Installation } from '../../../../models/configurators-2d/applications/fence/installations/installation';

/**
 * Service de calcul des panneaux de clôture.
 *
 * Ce service est responsable du calcul de la distribution des lames dans un panneau de clôture
 * en fonction de la hauteur disponible, des décorations horizontales et des configurations de pose.
 *
 * Il gère trois types de configurations :
 * - **Pose simple** : une seule installation avec une couleur de lame
 * - **Mixage de lames** : une seule installation avec deux tailles/couleurs de lames différentes
 *   (ex: Ajourée 135mm + Ajourée 98mm dans la même pose)
 * - **Mixage de poses** : deux installations différentes combinées
 *   (ex: Emboitée + Persiennée, Persiennée + Ajourée, Ajourée petit + Ajourée grand)
 *
 * Le calcul prend en compte :
 * - Les entretoises entre les poses (spacerHeightBetweenInstallations) déterminées par l'InstallationMixing
 * - Les entretoises de début/fin de chaque pose (startingSpacerHeight, finishingSpacerHeight)
 * - Les entretoises entre lames (spacerHeight) pour les poses ajourées/persiennées
 * - Le nombre de lames obligatoire par pose (boardNumber)
 * - Les décorations horizontales qui réduisent la hauteur disponible
 */
@Injectable({
  providedIn: 'root',
})
export class FencePanelCalculatorService {
  private readonly colorStateService = inject(ColorStateService);
  private readonly installationStateService = inject(InstallationStateService);
  private readonly installationMixingStateService = inject(InstallationMixingStateService);

  private readonly boardLength: number = 180;

  /**
   * Calcule la composition d'un panneau de clôture pour chaque pose (installation).
   *
   * Algorithme :
   * 1. Détermine l'entretoise entre les poses via l'InstallationMixing
   * 2. Calcule la hauteur disponible en soustrayant les entretoises de début/fin,
   *    l'entretoise inter-poses, la décoration et les entretoises des lames obligatoires
   * 3. Répartit la hauteur entre les poses : les poses avec boardNumber obligatoire
   *    consomment leur hauteur en priorité, le reste va à la pose secondaire
   * 4. Pour chaque pose, distribue les lames selon la hauteur qui lui est allouée
   *
   * @param height - La hauteur du poteau (en cm)
   * @param horizontalDecoration - La décoration horizontale (optionnelle)
   * @param configurationInstallations - Les configurations de pose (1 ou 2 max).
   *   - Pose simple : 1 installation, 1 board dans configurationBoards
   *   - Mixage de lames : 1 installation, 2 boards (le premier avec boardNumber fixe, le second remplit le reste)
   *   - Mixage de poses : 2 installations (la première avec boardNumber fixe, la seconde remplit le reste)
   *
   * @returns Un objet FencePanel contenant :
   *   - horizontalDecoration : la décoration horizontale (si fournie)
   *   - spacerHeightBetweenInstallations : hauteur de l'entretoise entre les deux poses
   *   - installationColors : tableau de FencePanelInstallationColors, trié par order décroissant
   *
   * @example
   * // Pose simple emboitée
   * calculate(183, undefined, [{installationId: 1, configurationBoards: [{boardId: 10}]}])
   * // → 1 installation, 12 lames (183 / 14.7 = 12)
   *
   * @example
   * // Mixage de lames (même installation, deux tailles de lames)
   * calculate(183, undefined, [{
   *   installationId: 3,
   *   configurationBoards: [
   *     {boardId: 14, boardNumber: 5}, // 5 lames 13.5cm fixes
   *     {boardId: 15} // lames 9.8cm remplissent le reste
   *   ]
   * }])
   * // → 1 installation, 5 lames 13.5cm + N lames 9.8cm
   *
   * @example
   * // Mixage de poses (deux installations différentes)
   * calculate(183, undefined, [
   *   {order: 0, installationId: 1, boardNumber: 9, configurationBoards: [{boardId: 10}]},
   *   {order: 1, installationId: 2, configurationBoards: [{boardId: 12}]}
   * ])
   * // → 2 installations : Emboitée 9 lames + Persiennée N lames (reste de hauteur)
   */
  calculate(
    height: number,
    horizontalDecoration: Decoration,
    configurationInstallations: ConfigurationInstallation[]
  ): FencePanel {
    // Gestion des cas null/undefined
    if (
      !height ||
      !configurationInstallations ||
      configurationInstallations.length === 0 ||
      configurationInstallations.some(ci => ci.configurationBoards.length === 0)
    ) {
      return this.createEmptySection();
    }

    // Gestion des cas où les poses ou les lames ne sont pas encore assignées
    if (
      configurationInstallations.some(ci => !ci.installationId) ||
      configurationInstallations.some(ci => ci.configurationBoards.some(cb => !cb.boardId))
    ) {
      return this.createEmptySection();
    }

    const spacerHeightBetweenInstallations = this.getSpacerHeightBetweenInstallations(configurationInstallations);

    if (spacerHeightBetweenInstallations === null) {
      return this.createEmptySection();
    }

    // Calcul de la hauteur disponible pour les lames
    let availableHeight = this.calculateAvailableHeight(
      height,
      configurationInstallations,
      spacerHeightBetweenInstallations,
      horizontalDecoration
    );

    const heightByConfigurationInstallation = this.calculateHeightByConfigurationInstallation(
      configurationInstallations,
      availableHeight
    );

    const installationColors: FencePanelInstallationColors[] = [];

    // Calcul de la distribution des lames pour chaque pose
    heightByConfigurationInstallation.forEach((height, configurationInstallation) => {
      installationColors.push(this.calculateByConfigurationInstallation(configurationInstallation, height));
    });

    // Construction de la section
    return {
      horizontalDecoration: horizontalDecoration,
      spacerHeightBetweenInstallations: spacerHeightBetweenInstallations,
      installationColors: installationColors,
    };
  }

  /**
   * Calcule la hauteur de l'entretoise entre deux poses.
   * @param configurationInstallations
   * @private
   */
  private getSpacerHeightBetweenInstallations(configurationInstallations: ConfigurationInstallation[]): number {
    if (configurationInstallations.length > 2) {
      return null;
    }

    // Pose simple, pas de d'entretoise à mettre entre les poses
    if (configurationInstallations.length === 1) {
      return 0;
    }

    let installationMixing = this.installationMixingStateService.get(
      configurationInstallations[0].installationId,
      configurationInstallations[1].installationId
    );

    // Installation est inversée si la lame avec l'order 2 est la lame principale du mixage
    const isReversed =
      configurationInstallations[0].order === 1 &&
      configurationInstallations[0].installationId === installationMixing.primaryInstallation.id;

    const primaryInstallationBoardId = configurationInstallations
      .find(ci => ci.installationId === installationMixing.primaryInstallation.id)
      .configurationBoards.find(cb => cb.order === 0).boardId;

    const secondaryInstallationBoardId = configurationInstallations
      .find(ci => ci.installationId === installationMixing.secondaryInstallation.id)
      .configurationBoards.find(cb => cb.order === 0).boardId;

    const primaryInstallationBoard = this.colorStateService.getByInstallationIdAndId(
      installationMixing.primaryInstallation.id,
      primaryInstallationBoardId
    );
    const secondaryInstallationBoard = this.colorStateService.getByInstallationIdAndId(
      installationMixing.secondaryInstallation.id,
      secondaryInstallationBoardId
    );

    const spacerToBeUsed = isReversed ? installationMixing.spacerToBeUsedReverse : installationMixing.spacerToBeUsed;

    // renvoie la hauteur de l'entretoise à utiliser
    switch (spacerToBeUsed) {
      case SpacerToBeUsed.PrimaryInstallationStartingSpacer:
        return primaryInstallationBoard.startingSpacerHeight ?? 0;

      case SpacerToBeUsed.SecondaryInstallationStartingSpacer:
        return secondaryInstallationBoard.startingSpacerHeight ?? 0;

      case SpacerToBeUsed.PrimaryInstallationFinishingSpacer:
        return primaryInstallationBoard.finishingSpacerHeight ?? 0;

      case SpacerToBeUsed.SecondaryInstallationFinishingSpacer:
        return secondaryInstallationBoard.finishingSpacerHeight ?? 0;

      case SpacerToBeUsed.PrimaryInstallationSpacer:
        return primaryInstallationBoard.spacerHeight ?? 0;

      case SpacerToBeUsed.SecondaryInstallationSpacer:
        return secondaryInstallationBoard.spacerHeight ?? 0;

      default:
        return 0;
    }
  }

  /**
   * Calculate la hauteur disponible pour chaque pose en prenant en compte les lames obligatoires
   * @param configurationInstallations
   * @param totalHeight
   * @private
   */
  private calculateHeightByConfigurationInstallation(
    configurationInstallations: ConfigurationInstallation[],
    totalHeight: number
  ): Map<ConfigurationInstallation, number> {
    let availableHeight = totalHeight;
    const heightByConfigurationInstallation = new Map<ConfigurationInstallation, number>();

    const configurationInstallationSort = [...configurationInstallations].sort(
      (a, b) => (b.boardNumber ?? 0) - (a.boardNumber ?? 0)
    );

    for (const configurationInstallation of configurationInstallationSort) {
      const requiredHeight = this.calculateConfigurationInstallationRequiredBoardHeight(
        configurationInstallation,
        availableHeight,
        true
      );

      if (requiredHeight > 0) {
        availableHeight -= requiredHeight;
        heightByConfigurationInstallation.set(configurationInstallation, Math.round(requiredHeight * 100) / 100);
        continue;
      }

      // Faut arrondir la hauteur disponible, le calcul des décimales est problématique en JS (arrondi avec deux décimales)
      heightByConfigurationInstallation.set(configurationInstallation, Math.round(availableHeight * 100) / 100);
    }

    // retrie par ordre d'affichage
    return new Map(
      [...heightByConfigurationInstallation.entries()].sort(([a], [b]) => (b.order ?? 0) - (a.order ?? 0))
    );
  }

  /**
   * Calcule la distribution des lames pour une installation spécifique.
   * @param configurationInstallation
   * @param availableHeight
   * @private
   */
  private calculateByConfigurationInstallation(
    configurationInstallation: ConfigurationInstallation,
    availableHeight: number
  ): FencePanelInstallationColors {
    const installation = this.installationStateService.getById(configurationInstallation.installationId);
    const colors = this.distributeColorsForInstallation(configurationInstallation, availableHeight);

    return {
      installation: installation,
      colors: colors,
    };
  }

  /**
   * Crée une section vide.
   *
   * Utilisée lorsque la hauteur fournie est nulle ou invalide.
   *
   * @returns Une FenceSection vide (sans décoration et sans installations)
   * @private
   */
  private createEmptySection(): FencePanel {
    return {
      horizontalDecoration: undefined,
      spacerHeightBetweenInstallations: undefined,
      installationColors: [],
    };
  }

  /**
   * Calcule la hauteur disponible pour les lames après déduction de tous les éléments non-lames.
   *
   * Déduit dans l'ordre :
   * 1. L'entretoise entre les deux poses (spacerHeightBetweenInstallations)
   * 2. Les entretoises de début/fin selon le nombre d'installations :
   *    - 1 installation : startingSpacerHeight + finishingSpacerHeight de la première lame
   *    - 2 installations : startingSpacerHeight de la 1ère pose (order 0) + finishingSpacerHeight de la 2ème (order 1)
   * 3. L'entretoise (spacerHeight) des lames obligatoires (boardNumber défini)
   * 4. La hauteur de la décoration horizontale
   *
   * @param height - La hauteur du poteau (en cm)
   * @param configurationInstallations - Les configurations des installations
   * @param spacerHeightBetweenInstallations - La hauteur de l'entretoise entre les poses
   * @param decoration - La décoration horizontale (optionnelle)
   * @returns La hauteur disponible pour les lames (toujours >= 0)
   * @private
   */
  private calculateAvailableHeight(
    height: number,
    configurationInstallations: ConfigurationInstallation[],
    spacerHeightBetweenInstallations: number,
    decoration?: Decoration
  ): number {
    height -= spacerHeightBetweenInstallations;

    height = this.calculateAvailableHeightWithInstallationWithSpacers(height, configurationInstallations);

    if (decoration != null) {
      height = this.calculateAvailableHeightForInstallationWithDecoration(height, decoration);
    }

    // Pose horizontal simple (une seule pose)
    if (configurationInstallations.length === 1) {
      height = this.calculateAvailableHeightForInstallation(height, configurationInstallations[0], true);

      return height;
    }

    // Pose horizontal mixte (deux poses)
    // calcule la hauteur que prennent les lames obligatoires
    const requiredHeight = this.calculateConfigurationInstallationRequiredBoardHeight(
      configurationInstallations.find(ci => ci.boardNumber),
      height,
      false
    );

    const isOtherInstallationInterlocking = configurationInstallations.some(ci => {
      const inst = this.installationStateService.getById(ci.installationId);
      return inst.installationType === InstallationTypeEnum.Interlocking;
    });

    const configurationInstallationSort = [...configurationInstallations].sort(
      (a, b) => (b.boardNumber ?? 0) - (a.boardNumber ?? 0)
    );

    for (const configurationInstallation of configurationInstallationSort) {
      // on retire de la hauteur disponible les pofilés/entretoises que si la pose a encore assez de place
      // ou si la pose est obligatoire
      if (height >= requiredHeight || configurationInstallation.boardNumber) {
        height = this.calculateAvailableHeightForInstallation(height, configurationInstallation, false, isOtherInstallationInterlocking);
      }
    }

    return Math.max(0, Math.round(height * 100) / 100);
  }

  /**
   * Calcule la hauteur disponible pour une pose
   * @param height
   * @param configurationInstallation
   * @param isSimpleInstallation
   * @private
   */
  private calculateAvailableHeightForInstallation(
    height: number,
    configurationInstallation: ConfigurationInstallation,
    isSimpleInstallation: boolean,
    isOtherInstallationInterlocking = false
  ): number {
    const installation = this.installationStateService.getById(configurationInstallation.installationId);

    switch (installation.installationType) {
      case InstallationTypeEnum.Shutter:
        return this.calculateAvailableHeightForShutterInstallation(height, configurationInstallation, installation);

      case InstallationTypeEnum.Interlocking:
        return this.calculateAvailableHeightForInterlockingInstallation(
          height,
          configurationInstallation,
          installation
        );

      case InstallationTypeEnum.VerticalInterlocking:
        return this.calculateAvailableHeightForVerticalInstallation();

      case InstallationTypeEnum.Openwork:
        return this.calculateAvailableHeightForOpenworkInstallation(
          height,
          configurationInstallation,
          installation,
          isSimpleInstallation,
          isOtherInstallationInterlocking
        );

        // Pour les poses "clôture acier", il n'y a rien à retirer de la hauteur disponible
      case InstallationTypeEnum.SteelFence:
        return height;

      default:
        throw new Error(`Le type d'installation ${installation.installationType} n'est pas pris en charge.`);
    }
  }

  /**
   * Calcule la hauteur disponible pour la pose d'une persienne
   * @param height
   * @param configurationInstallation
   * @param installation
   * @private
   */
  private calculateAvailableHeightForShutterInstallation(
    height: number,
    configurationInstallation: ConfigurationInstallation,
    installation: Installation
  ): number {
    if (installation.installationType !== InstallationTypeEnum.Shutter) {
      throw new Error(`La pose secondaire doit être une pose persiennée pour calculer la hauteur disponible.`);
    }

    const shutterConfigurationInstallationConfigurationBoard = configurationInstallation.configurationBoards?.[0];

    if (!shutterConfigurationInstallationConfigurationBoard) {
      throw new Error(`Impossible de trouver la configuration de la lame de la pose persiennée dans la configuration.`);
    }

    const shutterConfigurationInstallationBoard = this.colorStateService.getByInstallationIdAndId(
      installation.id,
      shutterConfigurationInstallationConfigurationBoard.boardId
    );

    if (!shutterConfigurationInstallationBoard) {
      throw new Error(`Impossible de trouver la lame de la pose persiennée dans la configuration.`);
    }

    const spacersHeight =
      shutterConfigurationInstallationBoard.startingSpacerHeight +
      shutterConfigurationInstallationBoard.finishingSpacerHeight;

    // On enlève de la hauteur l'entretoise de départ et de finition de la pose persiennée
    height -= spacersHeight;

    return height;
  }

  /**
   * Calcule la hauteur disponible pour la pose d'une emboitée
   * il faut retirer le profilé de départ et de finition de la hauteur
   * @param height
   * @param configurationInstallation
   * @param installation
   * @private
   */
  private calculateAvailableHeightForInterlockingInstallation(
    height: number,
    configurationInstallation: ConfigurationInstallation,
    installation: Installation
  ): number {
    if (installation.installationType != InstallationTypeEnum.Interlocking) {
      throw new Error('La pose secondaire doit être une pose emboîtée pour calculer la hauteur disponible.');
    }

    const interlockingConfigurationInstallationConfigurationBoard = configurationInstallation.configurationBoards?.[0];

    if (!interlockingConfigurationInstallationConfigurationBoard) {
      throw new Error('Impossible de trouver la configuration de la lame de la pose emboîtée dans la configuration.');
    }

    const interlockingConfigurationInstallationBoard = this.colorStateService.getByInstallationIdAndId(
      installation.id,
      interlockingConfigurationInstallationConfigurationBoard.boardId
    );

    if (!interlockingConfigurationInstallationBoard) {
      throw new Error('Impossible de trouver la lame de la pose emboîtée dans la configuration.');
    }

    const profilesHeight =
      interlockingConfigurationInstallationBoard.profile.topHeight +
      interlockingConfigurationInstallationBoard.profile.bottomHeight;

    // On enlève de la hauteur l'entretoise de départ et de finition de la pose persiennée
    height -= profilesHeight;

    return height;
  }

  /**
   * Calcule la hauteur disponible pour une pose verticale.
   * Toujours la longueur des lames (180cm)
   * @private
   */
  private calculateAvailableHeightForVerticalInstallation(): number {
    return this.boardLength;
  }

  /**
   * Calcule la hauteur disponible pour les poses ajourée
   * Retire l'entretoise de départ de l'autre pose (si pose secondaire en bas)
   * ou l'entretoise de finition de l'autre pose (si pose secondaire en haut)
   * ou retire l'entretoise de départ et de finition si la configuration est une pose simple
   * @param height
   * @param configurationInstallation
   * @param installation
   * @param isSimpleInstallation
   * @private
   */
  private calculateAvailableHeightForOpenworkInstallation(
    height: number,
    configurationInstallation: ConfigurationInstallation,
    installation: Installation,
    isSimpleInstallation: boolean,
    isOtherInstallationInterlocking = false
  ): number {
    const otherConfigurationInstallationConfigurationBoard = configurationInstallation.configurationBoards?.[0];

    if (!otherConfigurationInstallationConfigurationBoard) {
      throw new Error('Impossible de trouver la configuration de la lame de la pose emboîtée dans la configuration.');
    }

    const otherConfigurationInstallationBoard = this.colorStateService.getByInstallationIdAndId(
      installation.id,
      otherConfigurationInstallationConfigurationBoard.boardId
    );

    if (!otherConfigurationInstallationBoard) {
      throw new Error('Impossible de trouver la lame de la pose accompagnant la pose emboitée dans la configuration.');
    }

    const spacersHeight =
      otherConfigurationInstallationBoard.startingSpacerHeight +
      otherConfigurationInstallationBoard.finishingSpacerHeight;

    // c'est une pose simple
    if (isSimpleInstallation) {
      // on retire l'entretoise de départ et de finition
      height -= spacersHeight;
      return height;
    }

    // la pose ajourée est en bas
    if (configurationInstallation.order === 0) {
      // on retire l'entretoise de départ de la pose ajourée
      height -= otherConfigurationInstallationBoard.startingSpacerHeight ?? 0;
    }
    // la pose ajourée est en haut
    else if (configurationInstallation.order === 1) {
      // Quand la pose accompagnatrice est emboîtée, l'entretoise de départ (18mm) peut
      // être utilisée comme entretoise de finition, ce qui permet de maximiser le nombre
      // de lames ajourées
      if (isOtherInstallationInterlocking) {
        height -= otherConfigurationInstallationBoard.startingSpacerHeight ?? 0;
      } else {
        height -= otherConfigurationInstallationBoard.finishingSpacerHeight ?? 0;
      }
    }

    return height;
  }

  /**
   * Calcule la hauteur disponible pour une pose avec un décoration horizontale.
   * On retire de la hauteur disponible le décor horizontal
   * @param height
   * @param decoration
   * @private
   */
  private calculateAvailableHeightForInstallationWithDecoration(height: number, decoration: Decoration): number {
    return height - decoration.height;
  }

  /**
   * Calcule la hauteur disponible pour les poses avec des entretoises intermédiaires
   * Retire de la hauteur disponible les entretoises qui viennent se placer entre les lames
   * @param height
   * @param configurationInstallations
   * @private
   */
  private calculateAvailableHeightWithInstallationWithSpacers(
    height: number,
    configurationInstallations: ConfigurationInstallation[]
  ): number {
    // si la configuration contient une lame avec un nombre obligatoire à mettre et qu'elle a une hauteur d'entretoise
    // il faut alors retirer de la hauteur l'entretoise qui viendra se placer entre les deux lames
    const configurationInstallationsWithRequiredBoard = configurationInstallations.filter(ci =>
      ci.configurationBoards.some(cb => cb.boardNumber)
    );

    for (const configurationInstallation of configurationInstallationsWithRequiredBoard) {
      const configurationBoardRequired = configurationInstallation.configurationBoards.find(cb => cb.boardNumber);
      const boardRequired = this.colorStateService.getById(configurationBoardRequired.boardId);

      height -= boardRequired.spacerHeight ?? 0;
    }

    return height;
  }

  /**
   * Distribue les lames pour une installation donnée.
   *
   * Parcourt les configurationBoards triés par order :
   * - Si boardNumber est défini : ajoute exactement ce nombre de lames
   * - Sinon : calcule le nombre de lames qui remplissent la hauteur restante
   *   (après déduction de la hauteur des lames obligatoires)
   *
   * @param configurationInstallation - La configuration de l'installation contenant les lames à distribuer
   * @param availableHeight - La hauteur disponible pour cette installation (en cm)
   * @returns Un tableau de Color représentant chaque lame individuelle
   * @private
   */
  private distributeColorsForInstallation(
    configurationInstallation: ConfigurationInstallation,
    availableHeight: number
  ): Color[] {
    if (
      !configurationInstallation ||
      !configurationInstallation.configurationBoards ||
      configurationInstallation.configurationBoards.length === 0
    ) {
      return [];
    }

    // Récupère les objets Color depuis les boardIds
    const configurationColors = this.getColorsFromConfigurationBoards(configurationInstallation);

    // enlève la hauteur des lames qui seront forcément dans la pose
    if (configurationInstallation.configurationBoards.some(cb => cb.boardNumber)) {
      const configurationBoard = configurationInstallation.configurationBoards.find(cb => cb.boardNumber);
      const color = configurationColors.find(c => c.id === configurationBoard.boardId);

      availableHeight -= this.calculateRequiredBoardHeight(color, configurationBoard.boardNumber);
    }

    // Distribuer les lames selon les boardNumber
    const distributedColors: Color[] = [];

    const configurationBoardsSort = [...configurationInstallation.configurationBoards].sort(
      (a, b) => a.order - b.order
    );

    for (const configurationBoard of configurationBoardsSort) {
      const color = configurationColors.find(c => c.id === configurationBoard.boardId);

      if (!color) {
        continue;
      }

      if (configurationBoard.boardNumber) {
        // Nombre exact de lames spécifié, limité à ce qui rentre réellement dans la hauteur disponible
        let count = this.calculateBoardNumber(
          color,
          availableHeight + this.calculateRequiredBoardHeight(color, configurationBoard.boardNumber)
        );
        count = Math.min(configurationBoard.boardNumber, count);
        for (let i = 0; i < count; i++) {
          distributedColors.push(color);
        }
      } else {
        // Cette couleur remplit le reste des lames disponibles - celles réservées pour les autres couleurs
        const count = this.calculateBoardNumber(color, availableHeight);
        for (let i = 0; i < count; i++) {
          distributedColors.push(color);
        }
      }
    }

    return distributedColors;
  }

  /**
   * Calcule la hauteur que prennent les lames obligatoires pour une installation
   * @param configurationInstallation
   * @param availableHeight
   * @param fixInstallationHeightGreaterThanAvailableHeight si oui, on vérifie la hauteur calculée est inférieure à la hauteur disponible et on donne la hauteur des lames à la place
   * @private
   */
  private calculateConfigurationInstallationRequiredBoardHeight(
    configurationInstallation: ConfigurationInstallation,
    availableHeight: number,
    fixInstallationHeightGreaterThanAvailableHeight: boolean
  ): number {
    // Récupère les objets Color depuis les boardIds
    const configurationColors = this.getColorsFromConfigurationBoards(configurationInstallation);

    // on récupère la lame obligatoire pour cette installation (s'il y en a une)
    let configurationBoard = configurationInstallation.configurationBoards.find(cb => cb.boardNumber);

    // si on n'a pas de lame obligatoire, on récupère l'une des lames dans l'installation
    if (!configurationBoard) {
      configurationBoard = configurationInstallation.configurationBoards[0];
    }

    const requiredColorCount = configurationInstallation.boardNumber ?? 0;

    if (requiredColorCount <= 0) {
      return 0;
    }

    const requiredColor = configurationColors.find(c => c.id === configurationBoard.boardId);
    const spacerHeight = requiredColor?.spacerHeight ?? 0;

    if (!requiredColor) {
      return 0;
    }

    // Hauteur maximale de la lame obligatoire pour la pose
    let installationHeight = requiredColor.height * requiredColorCount + spacerHeight * (requiredColorCount - 1);

    // Dans certain cas la hauteur calculer dans la pose est plus grande que la hauteur disponible
    if (installationHeight <= availableHeight || !fixInstallationHeightGreaterThanAvailableHeight) {
      return installationHeight;
    }

    // on calcule l'espace en trop
    const extraSpace = installationHeight - availableHeight;
    const extraBoardAndSpacer = Math.ceil(extraSpace / (requiredColor.height + spacerHeight));

    // on retire le nombre de lames + entretoise nécessaire pour que la hauteur soit inférieur à la hauteur disponible
    installationHeight -= extraBoardAndSpacer * (requiredColor.height + spacerHeight);

    return installationHeight;
  }

  /**
   * Résout les objets Color complets à partir des ConfigurationBoard.
   *
   * Cette méthode récupère les objets Color depuis ColorStateService en utilisant
   * les boardId fournis dans les ConfigurationBoard.
   *
   * @param configurationInstallation - La configurationInstallation contenant les boardIds à résoudre
   * @returns Un tableau d'objets Color correspondants
   * @private
   */
  private getColorsFromConfigurationBoards(configurationInstallation: ConfigurationInstallation): Color[] {
    const configurationBoardIds = configurationInstallation.configurationBoards
      .filter(cb => cb.boardId)
      .map(b => b.boardId);
    return this.colorStateService.getByInstallationIdAndIds(
      configurationInstallation.installationId,
      configurationBoardIds
    );
  }

  private calculateBoardNumber(color: Color, availableHeight: number): number {
    const colorHeight = color.height;
    const spacerHeight = color?.spacerHeight ?? 0;

    if (!colorHeight || colorHeight <= 0 || availableHeight <= 0) {
      return 0;
    }

    // Calculer le nombre total de lames disponibles
    // On commence toujours à un, c'est la première lame sans entretoise
    let totalAvailableBoards = 1;
    availableHeight = Math.round((availableHeight - colorHeight) * 100) / 100;

    // Calculer la hauteur d'une "unité" (lame + entretoise si présente)
    const unitHeight = colorHeight + spacerHeight;

    // Calculer le nombre total de lames disponibles
    totalAvailableBoards += Math.floor(availableHeight / unitHeight);

    return totalAvailableBoards;
  }

  private calculateRequiredBoardHeight(color: Color, boardNumber: number): number {
    const colorHeight = color.height;
    const spacerHeight = color?.spacerHeight ?? 0;

    const totalColorHeight = colorHeight * boardNumber;
    const totalSpacerHeight = spacerHeight * (boardNumber - 1);

    return totalColorHeight + totalSpacerHeight;
  }
}
